export default class DateTime extends Date {

    constructor(value) {
        if (!value) {
            super()
            return
        }

        const str = String(value).trim()

        // If the string already carries a timezone offset, let the native parser handle it
        if (/[Zz]$|[+-]\d{2}:\d{2}$/.test(str)) {
            super(str)
            return
        }

        // No timezone info — treat the datetime as Mountain local time
        const match = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?/)

        if (match) {
            const year    = parseInt(match[1], 10)
            const month   = parseInt(match[2], 10) - 1
            const day     = parseInt(match[3], 10)
            const hours   = parseInt(match[4] ?? '0', 10)
            const minutes = parseInt(match[5] ?? '0', 10)
            const seconds = parseInt(match[6] ?? '0', 10)

            // Use a naive UTC timestamp to determine DST state in Mountain timezone
            const naiveUtc = Date.UTC(year, month, day, hours, minutes, seconds)
            const offset   = DateTime._getMountainOffsetMinutes(new Date(naiveUtc))

            // UTC = Mountain local time - offset  (offset is negative, e.g. -420 for MST)
            super(naiveUtc - (offset * 60 * 1000))
        } else {
            super(value)
        }
    }

    /**
     * Returns the UTC offset in minutes for America/Denver (Mountain timezone).
     * MST = -420, MDT = -360
     */
    static _getMountainOffsetMinutes(date) {
        try {
            const parts  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Denver', timeZoneName: 'short' }).formatToParts(date)
            const tzName = parts.find(p => p.type === 'timeZoneName')?.value
            return tzName === 'MDT' ? -360 : -420
        } catch {
            return -420
        }
    }

    /**
     * Formats a date value as a YYYY-MM-DDTHH:MM string in the America/Denver timezone,
     * suitable for use with datetime-local inputs.
     *
     * @param {string|Date} value
     * @returns {string|null}
     */
    static toDateTimeInput(value) {
        if (!value) return null;
        const d = new Date(value);
        if (isNaN(d)) return null;
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Denver',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
        }).formatToParts(d);
        const get = type => parts.find(p => p.type === type)?.value ?? '00';
        return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    }

    /**
     * Returns a full ISO 8601 string anchored to Mountain timezone.
     * e.g., "2026-04-10T00:00:00-06:00"
     * Used by the model setter to store the date.
     */
    toISOString() {
        if (isNaN(this.getTime())) return 'Invalid Date'

        const offset    = DateTime._getMountainOffsetMinutes(this)
        const absOffset = Math.abs(offset)
        const sign      = offset < 0 ? '-' : '+'
        const offsetStr = `${sign}${String(Math.floor(absOffset / 60)).padStart(2, '0')}:${String(absOffset % 60).padStart(2, '0')}`

        const dtf   = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Denver',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        })
        const parts = dtf.formatToParts(this)
        const get   = type => parts.find(p => p.type === type)?.value ?? '00'

        let hours = get('hour')
        if (hours === '24') hours = '00'

        return `${get('year')}-${get('month')}-${get('day')}T${hours}:${get('minute')}:${get('second')}${offsetStr}`
    }
}
